'use client';
import React, { useRef } from 'react';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
import { useModal } from '@/components/modal/ModalProvider';
import { useCampaign } from '@/app/CampaignContext';
import { getContentImageProps } from '@/lib/utils';
import { resolveImageAltText } from '@/lib/utils';
import { revealed } from '@/lib/animation/revealed';
import { useProduct } from '@/components/product/product-context';

export default function MicrositeHeroLargeBackgroundImage({ block, position }) {
  const {
    background_image,
    heading,
    subheading,
    hero_price,
    sale_callout,
    show_sale_callout,
    cta_label,
    uid
  } = block.microsite_hero_with_large_background_image?.reference[0];
  const { productData } = useProduct();
  const queenVariant = productData?.shopifyProduct?.variants?.find(
    (variant) => variant.title === 'Queen'
  );
  const desktopImage = background_image?.desktop_image;
  const mobileImage = background_image?.mobile_image;
  const tabletImage = background_image?.tablet_image;

  const { srcSet: desktopSrcSet } = getContentImageProps({
    image: desktopImage,
    sizes: '100vw'
  });
  const { srcSet: tabletSrcSet } = getContentImageProps({
    image: tabletImage,
    sizes: '100vw'
  });
  const { srcSet: mobileSrcSet, remainingProps } = getContentImageProps({
    image: mobileImage,
    priority: true,
    sizes: '100vw'
  });

  const subheadingHtml = jsonToHtml(subheading);

  const { showPriceDetails } = useModal();
  const priceDetailsButtonRef = useRef(null);
  const saleDetailsButtonRef = useRef(null);

  const campaign = useCampaign();
  const { pricing_details, promotional_disclaimer } = campaign || {};

  const handleScroll = () => {
    const element = document.querySelector(
      '[data-name="microsite-add-to-cart"]'
    );
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      id={`${uid}_${position}`}
      data-name="microsite-hero-large-background-image"
      className="relative z-0 min-h-svh lg:mx-5 lg:h-[90vh] lg:min-h-0 lg:pt-5"
    >
      <div className="relative flex min-h-svh flex-col lg:h-full lg:min-h-0">
        {/* Background Image */}
        <revealed.picture className="absolute inset-0 h-full w-full">
          <source media="(min-width: 1024px)" srcSet={desktopSrcSet} />
          <source media="(min-width: 768px)" srcSet={tabletSrcSet} />
          <img
            srcSet={mobileSrcSet}
            {...remainingProps}
            alt={resolveImageAltText(desktopImage)}
            fetchPriority="high"
            loading="eager"
            className="h-full w-full object-cover"
          />
        </revealed.picture>

        {/* Hero Text */}
        <revealed.div
          className="text-parlor-green relative top-26 z-10 flex flex-col items-center gap-4 px-4 text-center"
          fade
          distance="24rem"
        >
          <div className="preheading-r md:preheading-l">{heading}</div>

          <h1
            className="h2 md:h1"
            dangerouslySetInnerHTML={{ __html: subheadingHtml }}
          />
        </revealed.div>

        <div className="flex justify-center">
          {/* Sale & CTA - overlayed on image */}
          <revealed.div
            className={`bg-warm-pewter/60 absolute inset-x-5 bottom-24 z-10 flex flex-col gap-2 p-3 backdrop-blur-[2px] md:inset-x-auto md:flex-row md:gap-10 md:p-4 lg:bottom-11 lg:p-5 ${show_sale_callout ? 'lg:left-[100px]' : ''} lg:translate-x-0 xl:left-1/2 xl:-translate-x-1/2`}
            fade
            distance="24rem"
            rootMargin="0px 0px 5% 0px"
          >
            <div className="flex flex-col gap-1">
              <div className="text-parlor-green flex items-center gap-1">
                {/* Displaying queen variant details */}
                <p className="price-r-label">{queenVariant?.title}</p>
                <p className="price-r text-parlor-green">
                  ${parseFloat(queenVariant?.price.amount)}
                </p>
                <p className="price-r-crossed text-parlor-green">
                  ${parseFloat(queenVariant?.compareAtPrice.amount)}
                </p>
                <button
                  id="microsite-hero-cta"
                  ref={priceDetailsButtonRef}
                  className="body-xs ml-1 underline"
                  onClick={() => {
                    showPriceDetails(
                      `microsite-hero-large-background-image-pricing-modal`,
                      priceDetailsButtonRef,
                      pricing_details || ''
                    );
                  }}
                >
                  {hero_price.price_button_copy}
                </button>
              </div>
              <div className="text-dark-grey-text flex text-sm">
                {hero_price?.benefits?.map((benefit, index) => (
                  <React.Fragment key={benefit._metadata.uid || index}>
                    <p>{benefit.benefit_text}</p>
                    {index !== hero_price.benefits.length - 1 && (
                      <span className="mx-2">•</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div>
              <button
                onClick={handleScroll}
                className="text-button w-full bg-white px-20 py-3 md:px-10"
              >
                {cta_label}
              </button>
            </div>
          </revealed.div>
          {/* Sale Panel - BOTTOM RIGHT Corner */}
          {show_sale_callout && (
            <revealed.div
              className="bg-parlor-green z-10 hidden w-full flex-row justify-center gap-2 p-5 text-white lg:absolute lg:right-5 lg:bottom-6 lg:flex lg:w-[237px] lg:flex-col lg:items-start"
              from="right"
              delayMs={1500}
              durationMs={2000}
              fade
            >
              <p className="body-r">{sale_callout.sale_name}</p>
              <p className="sh11 lg:sh9">{sale_callout.sale_discount}</p>

              <button
                className="body-xs underline"
                ref={saleDetailsButtonRef}
                onClick={() => {
                  showPriceDetails(
                    `microsite-hero-large-background-image-sales-modal`,
                    saleDetailsButtonRef,
                    promotional_disclaimer?.disclaimer_content.campaign_terms ||
                      ''
                  );
                }}
              >
                {sale_callout.sale_button_copy}
              </button>
            </revealed.div>
          )}
        </div>

        {/* Mobile / Tablet Sale Panel (Below Hero Image Block) */}
        {show_sale_callout && (
          <revealed.div
            className="bg-parlor-green z-10 mt-auto flex w-full justify-center gap-2 p-5 text-white lg:hidden"
            rootMargin="0px 0px 5% 0px"
          >
            <p className="body-r">{sale_callout?.sale_name}</p>
            <p className="sh11">{sale_callout?.sale_discount}</p>

            <button
              className="body-xs underline"
              ref={saleDetailsButtonRef}
              onClick={() => {
                showPriceDetails(
                  `microsite-hero-large-background-image-sales-modal`,
                  saleDetailsButtonRef,
                  promotional_disclaimer?.disclaimer_content.campaign_terms ||
                    ''
                );
              }}
            >
              {sale_callout?.sale_button_copy}
            </button>
          </revealed.div>
        )}
      </div>
    </section>
  );
}
